import type { FocusEventHandler, JSX, MouseEventHandler, ReactNode } from 'react';
import React from 'react';

interface ITrackableEvents {
  onClick?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  // Easy to extend with more events in the future
}

interface ITrackableProps {
  children: ReactNode;
  events?: ITrackableEvents;
  // For simple cases where you just need one event
  onClick?: MouseEventHandler;
  onMouseEnter?: MouseEventHandler;
}

/**
 * Generic wrapper component that adds event tracking to any child component.
 * Provides a transparent wrapper that doesn't affect layout.
 *
 * Can be used directly for simple cases or as infrastructure for specialized
 * tracking components like TrackBlocked, TrackVisibility, etc.
 */
export function Trackable({
  children,
  events,
  onClick,
  onMouseEnter,
}: ITrackableProps): JSX.Element {
  // Merge simple props with events object
  const allEvents = {
    ...events,
    ...(onClick && { onClick }),
    ...(onMouseEnter && { onMouseEnter }),
  };

  // If no events are provided, return children directly
  if (!allEvents || Object.keys(allEvents).length === 0) {
    return <>{children}</>;
  }

  const wrapperStyle = { display: 'contents' } as const;

  return (
    <div {...allEvents} data-name={'trackable-wrapper'} style={wrapperStyle}>
      {children}
    </div>
  );
}

export default Trackable;
